import { useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useSEO } from "../lib/seo";
import { Mail, ShieldCheck, FlaskConical, DollarSign, Smartphone, Send, Loader2 } from "lucide-react";

export function About() {
  useSEO({ title: "About Us", description: "PoolServiceSoftwareGuide is an independent research site helping pool service companies choose the right software." });
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16" data-testid="about-page">
      <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tight text-slate-900">About PoolServiceSoftwareGuide</h1>
      <div className="prose-pool mt-8">
        <p>Choosing software is one of the biggest operational decisions a pool service company makes — and one of the hardest to undo. Migrations cost weeks; the wrong platform costs seasons.</p>
        <p>PoolServiceSoftwareGuide exists to make that decision easy. We research and compare every serious platform in the industry: pool-specific tools like Skimmer and Pool Brain, and general field-service platforms like Jobber and Housecall Pro that many pool companies adapt.</p>
        <h2>What makes us different</h2>
        <ul>
          <li><strong>Industry-specific.</strong> We evaluate software against the daily reality of a pool route: readings, dosages, service reports, and recurring billing — not generic feature checklists.</li>
          <li><strong>Independent.</strong> Rankings are never for sale. See our <Link to="/methodology">methodology</Link>.</li>
          <li><strong>Operator math.</strong> Our free <Link to="/tools">calculators</Link> help you quantify route profitability and technician capacity before buying anything.</li>
        </ul>
        <h2>Who this is for</h2>
        <p>Solo operators buying their first software, growing companies hitting the limits of spreadsheets, and established businesses planning a migration. If you service pools for a living, this site was built for you.</p>
      </div>
    </div>
  );
}

export function Methodology() {
  useSEO({ title: "Review Methodology", description: "How PoolServiceSoftwareGuide researches and scores pool service software: feature evaluation, official pricing research, and cited third-party ratings." });
  const criteria = [
    { icon: Smartphone, title: "Field usability (30%)", desc: "The tech mobile experience: speed of stop completion, reading entry, photos, and offline behavior. If techs won't use it, nothing else matters." },
    { icon: FlaskConical, title: "Pool-specific depth (25%)", desc: "Chemical tracking, dosage logging, LSI support, service reports, and route optimization quality." },
    { icon: DollarSign, title: "Billing & money (25%)", desc: "Recurring billing, autopay, payment fees, QuickBooks sync, and how fast you actually get paid." },
    { icon: ShieldCheck, title: "Trust & support (20%)", desc: "Data export freedom, pricing transparency, support responsiveness, and track record." },
  ];
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16" data-testid="methodology-page">
      <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tight text-slate-900">How We Review Software</h1>
      <div className="prose-pool mt-8">
        <p>Every review combines three inputs: (1) hands-on evaluation of each platform's features and mobile app, (2) pricing pulled directly from each vendor's official pricing page — not estimates, and (3) verified user ratings aggregated from independent review platforms like Capterra and G2, always cited with a link and the review count. We update reviews as pricing and features change.</p>
        <p>Our ranking reflects overall fit for a typical pool service company across the criteria below — it is our editorial judgment, not a paid placement. The star rating shown on each review is the platform's real score on Capterra (with its review count), linked so you can verify it yourself.</p>
      </div>
      <div className="mt-10 space-y-5">
        {criteria.map((c) => (
          <div key={c.title} className="bg-white rounded-2xl border border-border p-6 flex gap-4">
            <span className="w-11 h-11 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center shrink-0">
              <c.icon className="w-5 h-5" aria-hidden="true" />
            </span>
            <div>
              <h2 className="font-heading font-bold text-slate-900">{c.title}</h2>
              <p className="text-sm text-slate-600 mt-1 leading-relaxed">{c.desc}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="prose-pool mt-10">
        <h2>Independence & affiliate disclosure</h2>
        <p>We do not accept payment for rankings, placements, or review scores. Some links on this site are affiliate or referral links, and we may earn a commission at no additional cost to you if you sign up through them. This never influences our rankings, scores, or verdicts — placements are earned, not paid.</p>
      </div>
    </div>
  );
}

const contactSchema = z.object({
  name: z.string().trim().min(2, "Please enter your name.").max(120),
  email: z.string().trim().email("Please enter a valid email address.").max(200),
  topic: z.string().default("General question"),
  message: z.string().trim().min(5, "Please enter a short message.").max(5000),
  website: z.string().max(0).optional(), // honeypot — must stay empty
});

export function Contact() {
  useSEO({ title: "Contact", description: "Contact Pool Software Guide — questions, corrections, or software vendors requesting a review." });
  const [submitting, setSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({ resolver: zodResolver(contactSchema), defaultValues: { topic: "General question", website: "" } });

  const onSubmit = async (values) => {
    setSubmitting(true);
    try {
      await api.post("/contact", values);
      toast.success("Thanks — your message is on its way. We'll get back to you within two business days.");
      reset();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Something went wrong. Please email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-border bg-white px-4 py-2.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-400";

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16" data-testid="contact-page">
      <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tight text-slate-900">Contact</h1>
      <div className="prose-pool mt-8">
        <p>Questions about a review, corrections, or a platform you'd like us to review? We read everything.</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="mt-8 bg-white rounded-2xl border border-border p-6 sm:p-8 space-y-5" data-testid="contact-form" noValidate>
        {/* Honeypot: hidden from users, catches bots */}
        <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" {...register("website")} />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label htmlFor="c-name" className="block text-sm font-semibold text-slate-700 mb-1.5">Name</label>
            <input id="c-name" data-testid="contact-name" className={inputCls} placeholder="Your name" {...register("name")} />
            {errors.name && <p className="text-xs text-rose-600 mt-1">{errors.name.message}</p>}
          </div>
          <div>
            <label htmlFor="c-email" className="block text-sm font-semibold text-slate-700 mb-1.5">Email</label>
            <input id="c-email" type="email" data-testid="contact-email" className={inputCls} placeholder="you@company.com" {...register("email")} />
            {errors.email && <p className="text-xs text-rose-600 mt-1">{errors.email.message}</p>}
          </div>
        </div>

        <div>
          <label htmlFor="c-topic" className="block text-sm font-semibold text-slate-700 mb-1.5">Topic</label>
          <select id="c-topic" data-testid="contact-topic" className={inputCls} {...register("topic")}>
            <option>General question</option>
            <option>Correction to a review</option>
            <option>Vendor: request a review</option>
            <option>Advertising / partnership</option>
          </select>
        </div>

        <div>
          <label htmlFor="c-message" className="block text-sm font-semibold text-slate-700 mb-1.5">Message</label>
          <textarea id="c-message" rows={6} data-testid="contact-message" className={inputCls} placeholder="How can we help?" {...register("message")} />
          {errors.message && <p className="text-xs text-rose-600 mt-1">{errors.message.message}</p>}
        </div>

        <button type="submit" disabled={submitting} data-testid="contact-submit"
          className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-sky-500 text-white text-sm font-semibold hover:bg-sky-600 active:scale-95 transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed">
          {submitting ? <><Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Sending…</> : <>Send message <Send className="w-4 h-4" aria-hidden="true" /></>}
        </button>
      </form>

      <div className="mt-8 bg-sky-50 rounded-2xl border border-sky-100 p-6 flex items-center gap-4">
        <span className="w-11 h-11 rounded-xl bg-white text-sky-600 flex items-center justify-center shrink-0 border border-sky-100">
          <Mail className="w-5 h-5" aria-hidden="true" />
        </span>
        <div>
          <p className="font-heading font-bold text-slate-900">contact@poolservicesoftwareguide.com</p>
          <p className="text-sm text-slate-500 mt-1">Prefer email? Reach us directly — we typically respond within two business days.</p>
        </div>
      </div>

      <div className="prose-pool mt-8">
        <h2>For software vendors</h2>
        <p>Want your platform reviewed? Use the form above (choose “Vendor: request a review”) and send trial access and current pricing. We do not accept payment for reviews or ranking changes — see our <Link to="/methodology">methodology</Link>.</p>
      </div>
    </div>
  );
}
